/** Shared models for the 3-step "new request" wizard. */

export interface WizardRequestor {
  id: string;
  name: string;
  role?: string;
  isCurrentUser: boolean;
  practiceAreaCodes?: string[];
}

export interface WizardClientMatter {
  clientNumber: string;
  clientName: string;
  matterNumber: string;
  matterName: string;
  /** Combined `clientNumber-matterNumber` value sent to the API. */
  clientMatterNumber: string;
}

export interface WizardPreferred {
  id: string;
  name: string;
  role: string;
  /** Email address, when known. */
  email?: string;
  /** Seniority lookup code, when known. Used to score requested-count fulfilment. */
  seniorityCode?: string;
  /** Human-readable seniority label, when known. */
  seniority?: string;
}

export interface WizardInitialAssignment {
  employeeId: string;
  name: string;
  role: string;
  assignmentTypeCode: 'PRIMARY' | 'COVERAGE';
  /** Combined `clientNumber-matterNumber` values this resource is assigned to. */
  clientMatterNumbers: string[];
  /** Seniority lookup code, when known. Used to score requested-count fulfilment. */
  seniorityCode?: string;
  /** Human-readable seniority label, when known. */
  seniority?: string;
}

/** Synthetic seniority code meaning "no seniority preference". */
export const ANY_SENIORITY_CODE = 'ANY';

/** One requested-resource row: how many people of a given seniority. */
export interface WizardRequestedResource {
  count: number;
  /** Seniority lookup code, or ANY_SENIORITY_CODE when no preference. */
  seniorityCode: string;
}

export interface DescribeStepResult {
  description: string;
  requestType: string;
  requestors: WizardRequestor[];
  practiceAreaCodes: string[];
}

export interface ClientTimingStepResult {
  clientMatters: WizardClientMatter[];
  startDate: Date | null;
  endDate: Date | null;
}

export interface ResourcesStepResult {
  requestedResources: WizardRequestedResource[];
  preferred: WizardPreferred[];
}

export interface AssignmentsStepResult {
  initialAssignments: WizardInitialAssignment[];
  /**
   * Per-matter requested counts by seniority, keyed by clientMatterNumber.
   * Independent from the request-level `ResourcesStepResult.requestedResources`.
   */
  matterRequestedResources?: Record<string, WizardRequestedResource[]>;
}

/** Full wizard payload assembled by the shell at submit time. */
export interface WizardResult {
  describe: DescribeStepResult;
  clientTiming: ClientTimingStepResult;
  resources: ResourcesStepResult;
  assignments: AssignmentsStepResult;
}
